// Prior Arcade client (spec v1.0 §2): landing sheet, the map, the card. Plain DOM, no framework.
import type { Stage, Status } from "../db/schema";
import { match, wordCount } from "../match";
import { Canvas, layoutTree, type EdgeRef, type Node } from "./canvas";
import { daysAgo, empty, isoDay, load, save as storeSave, today, type Goal, type LocalState } from "./store";

const $ = <T extends HTMLElement = HTMLElement>(s: string) => document.querySelector<T>(s)!;
const esc = (s: unknown) => String(s ?? "").replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" })[c]!);
const STAGES: Stage[] = ["survey", "start", "refine", "master"];
const STAGE_LABEL: Record<Stage, string> = { survey: "Survey", start: "Start", refine: "Refine", master: "Master" };
const STATUSES: Status[] = ["new", "started", "comfortable", "deep"];
const STATUS_LABEL: Record<Status, string> = { new: "New", started: "Started", comfortable: "Comfortable", deep: "Deep" };
const NEXT_STAGE: Record<Status, Stage> = { new: "start", started: "refine", comfortable: "refine", deep: "master" };

// ---------------------------------------------------------------- data
type GSubject = { id: string; slug: string; name: string; parentId: string | null; learnable: boolean; aliases: string[]; provenance: string };
type GHelp = { from: string; to: string; stage: Stage; strength: number };
type GLens = { subjectId: string; slug: string; name: string; kind: string; provenance: string };
type Graph = { subjects: GSubject[]; helps: GHelp[]; lenses: GLens[] };
type Resource = { id: string; title: string; kind: string; url: string | null; author: string | null; summary: string | null; hours: number | null };
type Rec = { id: string; stage: Stage; why: string; fit: number | null; used: number; provenance: string; resource: Resource; top?: { id: string; body: string; helpful: number } | null; comments?: number };
type Comment = { id: string; body: string; helpful: number; provenance: string; at: string };
type Brief = { id: string; slug: string; name: string; summary: string; learnable: boolean; provenance: string };
type Card = {
  subject: Brief & { aliases: string[] };
  chain: { slug: string; name: string }[];
  children: Brief[];
  helps_in: { stage: Stage; strength: number; provenance: string; subject: Brief }[];
  helps_out: { stage: Stage; strength: number; provenance: string; subject: Brief }[];
  stages: Record<Stage, { picks: Rec[]; more: Rec[] }>;
  lenses: { id: string; slug: string; name: string; summary: string; kind: string; used: number; provenance: string; adds: LensRec[]; removes: LensRec[] }[];
  hard: number;
};
type LensRec = { lensId: string; stage: Stage; action: "add" | "remove"; why: string | null; insteadOf: string | null; resource: Resource };

const [graph, configRows] = await Promise.all([
  fetch("/api/graph").then((r) => r.json() as Promise<Graph>),
  fetch("/api/config").then((r) => r.json() as Promise<Record<string, { value: unknown }>>),
]);
const cv = (k: string, d: number) => (typeof configRows[k]?.value === "number" ? (configRows[k]!.value as number) : d);
const ZOOM_SUBJECTS = cv("map_subjects_zoom", 0.45), ZOOM_TOPICS = cv("map_topics_zoom", 0.9), PARSE_MIN_WORDS = cv("parse_min_words", 5);
const state: LocalState = load();
/** Every save also syncs to the account, when there is one (§2.6). */
const save = (s: LocalState) => {
  storeSave(s);
  scheduleSync();
};
const byId = new Map(graph.subjects.map((s) => [s.id, s]));
const bySlug = new Map(graph.subjects.map((s) => [s.slug, s]));
const helpsInto = new Map<string, GHelp[]>(), helpsFrom = new Map<string, GHelp[]>();
for (const h of graph.helps) {
  helpsInto.set(h.to, [...(helpsInto.get(h.to) ?? []), h]);
  helpsFrom.set(h.from, [...(helpsFrom.get(h.from) ?? []), h]);
}
const HARD = cv("helps_hard", 0.8);
const markOf = (slug: string): Status => state.marks[slug] ?? "new";
const known = (slug: string) => markOf(slug) === "comfortable" || markOf(slug) === "deep";
/** Fog (spec §2.5): a hard helps-first edge from something not yet known. */
const fogged = (s: GSubject) => (helpsInto.get(s.id) ?? []).some((h) => h.strength >= HARD && !known(byId.get(h.from)?.slug ?? ""));
const chainOf = (s: GSubject) => {
  const out: GSubject[] = [];
  let p = s.parentId ? byId.get(s.parentId) : undefined;
  while (p && out.length < 8) {
    out.unshift(p);
    p = p.parentId ? byId.get(p.parentId) : undefined;
  }
  return out;
};

// ---------------------------------------------------------------- you
/** A learning goal is a subject you work on, with the resources you use. It is separate from knowing it (the status mark). */
const goals = () => graph.subjects.filter((s) => state.goals[s.slug]);
const goalOf = (slug: string): Goal | undefined => state.goals[slug];
/** Rec id → title, kind, link, for the resources my goals use. Filled from /api/recs as needed. */
type RecCard = { id: string; title: string; kind: string; url: string | null };
const recCards = new Map<string, RecCard>();
async function loadRecCards(ids: string[]) {
  const missing = [...new Set(ids)].filter((id) => !recCards.has(id));
  if (!missing.length) return;
  const res = await fetch(`/api/recs?ids=${missing.join(",")}`).catch(() => null);
  if (!res?.ok) return;
  for (const r of ((await res.json()) as { recs: RecCard[] }).recs) recCards.set(r.id, r);
  renderPanel();
}
const did = (slug: string, day: string) => (state.days[day] ?? []).includes(slug);
function toggleDay(slug: string, day: string) {
  const set = new Set(state.days[day] ?? []);
  if (set.has(slug)) set.delete(slug);
  else set.add(slug);
  if (set.size) state.days[day] = [...set];
  else delete state.days[day];
  save(state);
  renderPanel();
  renderMap();
}
function toggleGoal(slug: string) {
  if (state.goals[slug]) delete state.goals[slug];
  else state.goals[slug] = { since: today(), using: [], done: [] };
  save(state);
  renderPanel();
  renderMap();
}
/** Use this: attach a resource to my goal on this subject, starting the goal if it is new. Again takes it off. */
function useRec(slug: string, rec: Rec) {
  const isNew = !state.goals[slug];
  const g = (state.goals[slug] ??= { since: today(), using: [], done: [] });
  if (g.using.includes(rec.id)) g.using = g.using.filter((x) => x !== rec.id);
  else {
    g.using.push(rec.id);
    g.done = g.done.filter((x) => x !== rec.id);
    recCards.set(rec.id, { id: rec.id, title: rec.resource.title, kind: rec.resource.kind, url: rec.resource.url });
    if (isNew) toast(`Added as a learning goal. Tick each day you work on it.`);
  }
  save(state);
  renderPanel();
  renderMap();
}
/** Finished: the resource moves to done and counts as used for everyone (spec §1.4). Again puts it back. */
function finishRec(slug: string, recId: string) {
  const g = state.goals[slug];
  if (!g) return;
  if (g.done.includes(recId)) {
    g.done = g.done.filter((x) => x !== recId);
    g.using.push(recId);
  } else {
    g.using = g.using.filter((x) => x !== recId);
    g.done.push(recId);
    if (!state.used[recId]) {
      state.used[recId] = today();
      void post("/api/used", { rec_id: recId });
    }
  }
  save(state);
  renderPanel();
  renderMap();
}
/** "3 day streak" (consecutive ticks ending today or yesterday), else "4 days ago", else "". */
function paceBy(has: (day: string) => boolean, last: string | undefined): string {
  let n = 0;
  for (let i = has(today()) ? 0 : 1; has(daysAgo(i)); i++) n++;
  if (n > 0) return `${n} day streak`;
  if (!last) return "";
  const ago = Math.round((new Date(today()).getTime() - new Date(last).getTime()) / 864e5);
  return ago === 0 ? "today" : ago === 1 ? "yesterday" : `${ago} days ago`;
}
/** My pace on one subject, or on anything when slug is undefined. */
function pace(slug?: string): string {
  const days = Object.keys(state.days)
    .filter((d) => !slug || did(slug, d))
    .sort();
  return paceBy((d) => (slug ? did(slug, d) : (state.days[d]?.length ?? 0) > 0), days[days.length - 1]);
}
/** Pace over a sorted list of days, as a person's public view gives them. */
function paceOf(days: string[]): string {
  const set = new Set(days);
  return paceBy((d) => set.has(d), days[days.length - 1]);
}
function youSummary(): string {
  const list = goals();
  if (!list.length) return "No learning goals yet";
  const names = list.slice(0, 3).map((s) => s.name).join(", ") + (list.length > 3 ? ` +${list.length - 3}` : "");
  const p = pace();
  return p ? `${names} · ${p}` : names;
}

/** Fourteen tiny cells, today last: the at-a-glance version of the strip, for the cards on the map. */
function miniHTML(has: (day: string) => boolean) {
  let out = "";
  for (let i = 13; i >= 0; i--) out += `<i class="${has(daysAgo(i)) ? "on" : ""}"></i>`;
  return `<span class="mini">${out}</span>`;
}
/** One line per goal: name, the fortnight, pace. Shared by the centre card and the cards of people on my board. */
function goalLinesHTML(items: { name: string; has: (day: string) => boolean; days: string[] }[], max = 5) {
  const lines = items.slice(0, max).map((g) => `<div class="goal"><span class="n">${esc(g.name)}</span>${miniHTML(g.has)}<span class="p">${esc(paceOf(g.days))}</span></div>`);
  if (items.length > max) lines.push(`<div class="goal"><span class="n">+${items.length - max} more</span></div>`);
  return lines.join("");
}
/** GitHub-style heatmap of the last 20 weeks: one cell a day, darker with more subjects. */
function heatHTML(count: (iso: string) => number = (iso) => state.days[iso]?.length ?? 0, weeks = 20) {
  const end = new Date();
  end.setHours(0, 0, 0, 0);
  const start = new Date(end);
  start.setDate(end.getDate() - (weeks * 7 - 1) - end.getDay());
  let cells = "";
  const months: string[] = [];
  for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
    const iso = isoDay(d);
    const n = count(iso);
    if (d.getDate() === 1 || d.getTime() === start.getTime()) months.push(`<span style="grid-column:${Math.floor((d.getTime() - start.getTime()) / 864e5 / 7) + 1}">${d.toLocaleString(undefined, { month: "short" })}</span>`);
    cells += `<i class="l${Math.min(n, 3)}" title="${iso}${n ? ` · ${n}` : ""}"></i>`;
  }
  return `<div class="heat"><div class="months">${months.join("")}</div><div class="cells">${cells}</div></div>`;
}

/** The last two weeks for one subject, each day a toggle, so yesterday can be fixed. Someone else's is read-only. */
function stripHTML(slug: string, has: (day: string) => boolean = (day) => did(slug, day), editable = true) {
  let out = "";
  for (let i = 13; i >= 0; i--) {
    const day = daysAgo(i);
    const d = new Date(day + "T00:00:00");
    const title = d.toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric" });
    const cls = `day ${has(day) ? "on" : ""} ${i === 0 ? "today" : ""}`;
    out += editable ? `<button class="${cls}" data-day="${day}" data-day-slug="${slug}" title="${title}"><span>${"SMTWTFS"[d.getDay()]}</span></button>` : `<span class="${cls}" title="${title}"><span>${"SMTWTFS"[d.getDay()]}</span></span>`;
  }
  return `<div class="strip">${out}</div>`;
}

// ---------------------------------------------------------------- account (spec §2.6) and board (§2.2b)
type Me = { handle: string | null; email: string; public: boolean };
type Doc = Pick<LocalState, "marks" | "used" | "helpful" | "goals" | "days" | "lensesOn" | "asked" | "showGenerated">;
type PersonGoal = { slug: string; name: string; since: string; using: RecCard[]; done: RecCard[]; days: string[] };
type Person = { handle: string; goals: PersonGoal[]; days: Record<string, number> };
const SYNCED_KEY = "priorarcade:synced"; // the email this browser's document was united with
let me: Me | null = null;
let people: Person[] = [];
let syncTimer = 0;
function scheduleSync() {
  if (!me) return;
  clearTimeout(syncTimer);
  syncTimer = setTimeout(() => void pushDoc(), 1500) as unknown as number;
}
/** Send my document. With merge, the server unions it with what it has and returns the union. */
async function pushDoc(merge = false) {
  if (!me) return;
  clearTimeout(syncTimer);
  syncTimer = 0;
  const res = await fetch("/api/me/doc", { method: "PUT", headers: { "content-type": "application/json" }, body: JSON.stringify({ doc: state, merge }), keepalive: true }).catch(() => null);
  if (!res?.ok) return;
  if (merge) adopt(((await res.json()) as { doc: Doc }).doc);
}
/** Take the account's document as mine. Positions and recent stay local. */
function adopt(doc: Doc) {
  const { marks, used, helpful, goals, days, lensesOn, asked, showGenerated } = doc;
  Object.assign(state, { marks, used, helpful, goals, days, lensesOn, asked, showGenerated });
  storeSave(state);
}
async function loadMe() {
  const res = await fetch("/api/me").catch(() => null);
  if (!res?.ok) return;
  const json = (await res.json()) as { user: Me | null; doc: Doc | null };
  me = json.user;
  if (!me) return;
  let synced: string | null = null;
  try {
    synced = localStorage.getItem(SYNCED_KEY);
  } catch {}
  if (synced !== me.email) {
    // first sign-in on this browser: nothing here is lost
    await pushDoc(true);
    try {
      localStorage.setItem(SYNCED_KEY, me.email);
    } catch {}
  } else if (json.doc) adopt(json.doc);
  await loadBoard();
}
async function loadBoard() {
  people = [];
  if (me) {
    const res = await fetch("/api/me/board").catch(() => null);
    if (res?.ok) people = ((await res.json()) as { people: Person[] }).people;
  }
  placePeople();
  renderMap();
}
async function signOut(deleted = false) {
  await post(deleted ? "/api/me/delete" : "/api/auth/logout", {});
  me = null;
  personOpen = null;
  try {
    localStorage.removeItem(SYNCED_KEY);
  } catch {}
  Object.assign(state, empty());
  storeSave(state);
  await loadBoard();
  closeAccount();
  if (homeOpen) closePanel();
  toast(deleted ? "Account deleted" : "Signed out");
}
addEventListener("pagehide", () => {
  if (syncTimer) void pushDoc();
});

/** People on my board sit in a ring around You, inside the domains. */
const personNodes = new Map<string, Node>();
function placePeople() {
  for (const [id] of personNodes) {
    if (people.some((p) => `@${p.handle}` === id)) continue;
    personNodes.delete(id);
    els.get(id)?.remove();
    els.delete(id);
  }
  people.forEach((p, i) => {
    const id = `@${p.handle}`;
    let n = personNodes.get(id);
    if (!n) {
      n = { id, depth: 1, w: 220, h: 64, x: 0, y: 0, tx: 0, ty: 0, children: [] };
      personNodes.set(id, n);
    }
    const kept = state.positions[id];
    const a = -Math.PI / 2 + (i * 2 * Math.PI) / people.length;
    [n.tx, n.ty] = kept ?? [Math.cos(a) * 280, Math.sin(a) * 280];
  });
  canvas.nodes = [root, ...nodeById.values(), ...personNodes.values()];
  canvas.kick();
}

// ---------------------------------------------------------------- map
const els = new Map<string, HTMLElement>();
const canvas = new Canvas($("#viewport"), $("#world"), $<HTMLElement>("#edges") as unknown as SVGSVGElement, els);
const nodesEl = $("#nodes"), edgesEl = $("#edges");
const root: Node = { id: "root", depth: 0, w: 230, h: 84, x: 0, y: 0, tx: 0, ty: 0, fixed: true, children: [] };
const nodeById = new Map<string, Node>();
for (const s of graph.subjects) nodeById.set(s.id, { id: s.id, depth: 0, w: 200, h: 70, x: 0, y: 0, tx: 0, ty: 0, children: [] });
for (const s of graph.subjects) {
  const n = nodeById.get(s.id)!;
  const parent = s.parentId ? nodeById.get(s.parentId) : undefined;
  (parent ?? root).children.push(n);
  n.parent = parent ?? root;
}
const setDepth = (n: Node, d: number) => {
  n.depth = d;
  n.w = d === 1 ? 250 : d === 2 ? 196 : 160;
  n.h = d === 1 ? 96 : d === 2 ? 64 : 44;
  n.children.forEach((c) => setDepth(c, d + 1));
};
setDepth(root, 0);
root.w = 230;
root.h = 84;
canvas.nodes = [root, ...nodeById.values()];
const pinned = new Map<string, [number, number]>();
for (const [slug, pos] of Object.entries(state.positions)) {
  const s = bySlug.get(slug);
  if (s) pinned.set(s.id, pos);
}
/** Dependency rank among siblings: longest helps-first chain from a sibling into this subject. */
function siblingRanks(): (id: string) => number {
  const memo = new Map<string, number>();
  const rank = (id: string, seen = new Set<string>()): number => {
    const m = memo.get(id);
    if (m !== undefined) return m;
    if (seen.has(id)) return 0;
    seen.add(id);
    const me = byId.get(id);
    let best = 0;
    for (const h of helpsInto.get(id) ?? []) {
      const from = byId.get(h.from);
      if (!from || from.parentId !== me?.parentId || from.id === id) continue;
      best = Math.max(best, 1 + rank(from.id, seen));
    }
    memo.set(id, best);
    return best;
  };
  return (id) => rank(id);
}
function layout() {
  layoutTree(root, [560, 400, 260], pinned, siblingRanks());
  canvas.kick();
}

// tree edges (always), helps edges (only around the focused subject)
let treeEdges: EdgeRef[] = [], helpEdges: (EdgeRef & { h: GHelp })[] = [];
function buildEdges() {
  edgesEl.innerHTML = "";
  treeEdges = [];
  helpEdges = [];
  for (const s of graph.subjects) {
    if (!s.parentId) continue;
    const a = nodeById.get(s.parentId), b = nodeById.get(s.id);
    if (!a || !b) continue;
    const el = document.createElementNS("http://www.w3.org/2000/svg", "path");
    edgesEl.appendChild(el);
    treeEdges.push({ a, b, el });
  }
  if (!edgesEl.querySelector("defs")) {
    edgesEl.insertAdjacentHTML("afterbegin", '<defs><marker id="arrow" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse"><path d="M0,0 L10,5 L0,10 z" fill="var(--accent)"/></marker></defs>');
  }
  for (const h of graph.helps) {
    const a = nodeById.get(h.from), b = nodeById.get(h.to);
    if (!a || !b) continue;
    const el = document.createElementNS("http://www.w3.org/2000/svg", "path");
    el.classList.add("helps", "gone", h.strength >= HARD ? "needed" : "soft");
    el.setAttribute("marker-end", "url(#arrow)");
    edgesEl.appendChild(el);
    helpEdges.push({ a, b, el, h });
  }
  canvas.edges = [...treeEdges, ...helpEdges];
}

let focused: GSubject | null = null;
let zoomLevel = 1; // 1 domains, 2 subjects, 3 topics
function neighbourhood(s: GSubject): Set<string> {
  const ids = new Set<string>([s.id]);
  if (s.parentId) ids.add(s.parentId);
  for (const x of graph.subjects) if (x.parentId === s.parentId || x.parentId === s.id) ids.add(x.id);
  for (const h of helpsInto.get(s.id) ?? []) ids.add(h.from);
  for (const h of helpsFrom.get(s.id) ?? []) ids.add(h.to);
  return ids;
}

/** The centre card, the panel and the header all call me by my username once I have one. */
const myName = () => (me?.handle ? `@${me.handle}` : "You");
function renderMap() {
  const hood = focused ? neighbourhood(focused) : null;
  $("#you").textContent = me ? (me.handle ? `@${me.handle}` : "Account") : "Sign in";
  {
    let el = els.get("root");
    if (!el) {
      el = document.createElement("div");
      el.dataset.id = "root";
      nodesEl.appendChild(el);
      els.set("root", el);
    }
    el.className = `node home${homeOpen ? " sel" : ""}${hood ? " far" : ""}`;
    const mine = goals().map((g) => ({ name: g.name, has: (d: string) => did(g.slug, d), days: Object.keys(state.days).filter((d) => did(g.slug, d)).sort() }));
    el.innerHTML = `<div class="head"><div class="row"><div class="title">${esc(myName())}</div></div>${mine.length ? `<div class="goals">${goalLinesHTML(mine)}</div>` : `<div class="sub">${esc(youSummary())}</div>`}</div>`;
    root.h = el.offsetHeight || root.h;
  }
  for (const s of graph.subjects) {
    const n = nodeById.get(s.id)!;
    let el = els.get(s.id);
    if (!el) {
      el = document.createElement("div");
      el.dataset.id = s.id;
      nodesEl.appendChild(el);
      els.set(s.id, el);
    }
    const visible = n.depth <= zoomLevel || (hood?.has(s.id) ?? false);
    const cls = ["node", `d${Math.min(n.depth, 3)}`];
    if (!visible) cls.push("hide");
    if (hood && !hood.has(s.id)) cls.push("far");
    if (s.learnable && fogged(s)) cls.push("fog");
    if (focused?.id === s.id) cls.push("sel");
    const m = markOf(s.slug);
    if (m !== "new") cls.push(`mark-${m}`);
    el.className = cls.join(" ");
    const kids = graph.subjects.filter((x) => x.parentId === s.id);
    const sub = n.depth === 1 ? kids.slice(0, 5).map((k) => k.name).join(" · ") + (kids.length > 5 ? " · …" : "") : m !== "new" ? STATUS_LABEL[m].toLowerCase() : "";
    el.innerHTML = `<div class="head"><div class="row"><div class="title ${s.provenance === "generated" && n.depth > 1 ? "gen" : ""}">${esc(s.name)}</div></div>${sub ? `<div class="sub">${esc(sub)}</div>` : ""}</div>`;
    n.h = el.offsetHeight || n.h;
  }
  for (const p of people) {
    const id = `@${p.handle}`, n = personNodes.get(id)!;
    let el = els.get(id);
    if (!el) {
      el = document.createElement("div");
      el.dataset.id = id;
      nodesEl.appendChild(el);
      els.set(id, el);
    }
    el.className = `node person d2${personOpen === p.handle ? " sel" : ""}${hood ? " far" : ""}`;
    const theirs = p.goals.map((g) => ({ name: g.name, has: (d: string) => g.days.includes(d), days: g.days }));
    el.innerHTML = `<div class="head"><div class="row"><div class="title">@${esc(p.handle)}</div></div>${theirs.length ? `<div class="goals">${goalLinesHTML(theirs, 4)}</div>` : `<div class="sub">No learning goals yet</div>`}</div>`;
    n.h = el.offsetHeight || n.h;
  }
  for (const e of treeEdges) {
    const b = byId.get(e.b.id)!, nb = nodeById.get(b.id)!;
    const shown = nb.depth <= zoomLevel || (hood?.has(b.id) ?? false);
    e.el.classList.toggle("gone", !shown);
    e.el.classList.toggle("dim", Boolean(hood && !(hood.has(e.a.id) && hood.has(e.b.id))));
  }
  for (const e of helpEdges) e.el.classList.toggle("gone", !focused || !(e.h.from === focused.id || e.h.to === focused.id));
  canvas.drawEdges();
}
canvas.onZoom = (k) => {
  const z = k >= ZOOM_TOPICS ? 3 : k >= ZOOM_SUBJECTS ? 2 : 1;
  if (z !== zoomLevel) {
    zoomLevel = z;
    renderMap();
  }
};

// ---------------------------------------------------------------- card
let card: Card | null = null;
let homeOpen = false;
let personOpen: string | null = null;
let stage: Stage = "start";
let showMore = false;
let recOpen = false;
const KINDS = ["book", "course", "video", "article", "docs", "repo", "project", "other"] as const;
async function open(slug: string, opts: { center?: boolean } = {}) {
  const s = bySlug.get(slug);
  const res = await fetch(`/api/subject/${encodeURIComponent(slug)}`);
  if (!res.ok) return toast("not found");
  card = (await res.json()) as Card;
  homeOpen = false;
  personOpen = null;
  showMore = false;
  recOpen = false;
  openThreads.clear();
  stage = NEXT_STAGE[markOf(slug)];
  if (!card.subject.learnable && !card.stages.start.picks.length) stage = card.stages.survey.picks.length ? "survey" : "refine";
  if (!card.stages[stage].picks.length) stage = STAGES.find((st) => card!.stages[st].picks.length) ?? stage;
  focused = s ?? null;
  state.recent = [slug, ...state.recent.filter((x) => x !== slug)].slice(0, 12);
  save(state);
  document.body.classList.add("panel-open");
  closeAsk();
  renderPanel();
  renderMap();
  if (s && opts.center !== false) {
    const n = nodeById.get(s.id)!;
    canvas.centerNode(n, Math.max(canvas.cam.tk, n.depth >= 3 ? ZOOM_TOPICS : ZOOM_SUBJECTS + 0.05));
  }
  history.replaceState(null, "", `#${slug}`);
}
function closePanel() {
  card = null;
  homeOpen = false;
  personOpen = null;
  focused = null;
  document.body.classList.remove("panel-open");
  history.replaceState(null, "", location.pathname);
  renderMap();
}

const kindWord: Record<string, string> = { book: "book", course: "course", video: "video", article: "article", docs: "docs", repo: "repo", project: "project", other: "" };
function pickHTML(r: Rec, opts: { gone?: boolean; lens?: string; note?: string } = {}) {
  const res = r.resource;
  const g = card ? goalOf(card.subject.slug) : undefined;
  const using = g?.using.includes(r.id) ?? false, done = g?.done.includes(r.id) ?? false;
  const meta = [kindWord[res.kind] ?? "", res.author ?? "", res.hours ? `${res.hours}h` : "", opts.note ?? ""].filter(Boolean).join(" · ");
  const cls = [opts.gone ? "gone" : "", opts.lens ? "via-lens" : "", using ? "using" : "", done ? "done" : ""].filter(Boolean).join(" ");
  const useBtn = opts.gone || r.id.startsWith("lens:") ? "" : done ? `<button class="use done" data-use="${r.id}" title="Finished">✓ Finished</button>` : `<button class="use ${using ? "on" : ""}" data-use="${r.id}" title="${using ? "Stop using" : "Add to my learning goal"}">${using ? "Using" : "Use this"}</button>`;
  return `<li class="${cls}" data-rec="${r.id}">
    <div class="body"><div class="t ${r.provenance === "generated" ? "gen" : ""}">${res.url ? `<a href="${esc(res.url)}" target="_blank" rel="noopener">${esc(res.title)}</a>` : esc(res.title)}${opts.lens ? ` <span class="lens-tag">${esc(opts.lens)}</span>` : ""}</div>
    <div class="why">${esc(r.top?.body ?? r.why)}</div>${meta ? `<div class="m">${esc(meta)}</div>` : ""}
    ${r.id.startsWith("lens:") ? "" : `<a class="thread-open" data-thread="${r.id}">${openThreads.has(r.id) ? "close" : r.comments ? `${r.comments} comment${r.comments === 1 ? "" : "s"}` : "comment"}</a>`}
    ${openThreads.has(r.id) ? threadHTML(r) : ""}</div>
    ${useBtn}
  </li>`;
}

const openThreads = new Set<string>();
const threads = new Map<string, Comment[]>();
function threadHTML(r: Rec) {
  const list = threads.get(r.id);
  const rows = list
    ? list
        .map((cm) => {
          const mine = Boolean(state.helpful[cm.id]);
          return `<li class="${cm.provenance === "generated" ? "gen" : ""}"><span>${esc(cm.body)}</span><button class="helpful ${mine ? "on" : ""}" data-helpful="${cm.id}" title="helpful">${cm.helpful > 0 ? cm.helpful : ""}<i>▲</i></button></li>`;
        })
        .join("")
    : '<li class="muted">…</li>';
  return `<div class="thread">
    <ul>${list && !list.length ? `<li class="gen">${esc(r.why)}</li>` : rows}</ul>
    <form class="thread-add" data-thread-form="${r.id}"><input name="body" placeholder="your take, in a line" maxlength="280" autocomplete="off"><button class="btn quiet" type="submit">add</button></form>
    <div class="thread-foot">${(() => {
      const g = card ? goalOf(card.subject.slug) : undefined;
      const usedIt = Boolean(state.used[r.id]);
      const fin = g?.done.includes(r.id) ? "Finished" : g?.using.includes(r.id) ? `<a data-finish="${r.id}">Finished</a>` : "";
      return `${fin ? fin + " · " : ""}<a data-used="${r.id}" class="${usedIt ? "on" : ""}">${usedIt ? "✓ I used this" : "I used this"}</a> · `;
    })()}<a data-flag="${r.id}">flag</a><span class="flag-reasons" hidden><a data-flag-reason="dead-link">dead link</a> · <a data-flag-reason="wrong-subject">wrong subject</a> · <a data-flag-reason="spam">spam</a></span></div>
  </div>`;
}
async function loadThread(recId: string) {
  const res = await fetch(`/api/rec/${encodeURIComponent(recId)}/comments`);
  if (!res.ok) return;
  threads.set(recId, ((await res.json()) as { comments: Comment[] }).comments);
  renderPanel();
}

const openStrips = new Set<string>();
function renderHome() {
  const mine = goals();
  void loadRecCards(mine.flatMap((g) => [...state.goals[g.slug]!.using, ...state.goals[g.slug]!.done]));
  const list = mine
    .sort((a, b) => (state.goals[a.slug]!.since < state.goals[b.slug]!.since ? 1 : -1))
    .map((s) => {
      const g = state.goals[s.slug]!, open = openStrips.has(s.slug);
      const res = [...g.using.map((id) => ({ id, done: false })), ...g.done.map((id) => ({ id, done: true }))]
        .map(({ id, done }) => {
          const r = recCards.get(id);
          if (!r) return "";
          return `<li class="${done ? "done" : ""}">${r.url ? `<a href="${esc(r.url)}" target="_blank" rel="noopener">${esc(r.title)}</a>` : esc(r.title)}<span class="m">${esc(done ? "finished" : (kindWord[r.kind] ?? ""))}</span></li>`;
        })
        .join("");
      return `<li class="${open ? "open" : ""}"><div class="row" data-strip="${s.slug}"><span class="go">${esc(s.name)}</span><span class="pace">${esc(pace(s.slug))}</span><button class="day today-btn ${did(s.slug, today()) ? "on" : ""}" data-day="${today()}" data-day-slug="${s.slug}" title="Worked on it today">${did(s.slug, today()) ? "✓ Today" : "Today"}</button><a class="goto" data-open="${s.slug}" title="Open">›</a></div>${res ? `<ul class="using-list">${res}</ul>` : ""}${open ? stripHTML(s.slug) : ""}</li>`;
    })
    .join("");
  const anyDay = Object.keys(state.days).length > 0;
  $("#panel-body").innerHTML = `
    <div class="panel-top"><h2>${esc(myName())}</h2>${me ? `<a class="acct-link" data-account>Account</a>` : ""}</div>
    <p class="summary">${esc(youSummary())}</p>
    <h3>Learning goals</h3>
    ${list ? `<ul class="focus">${list}</ul><div class="hint">Tap a goal to see and edit the last two weeks.</div>` : '<p class="summary">Open a subject and tap <b>Use this</b> on a resource, or <b>Add as a learning goal</b>.</p>'}
    ${anyDay ? heatHTML() : ""}
    ${me ? boardHTML() : `<div class="hint acct-foot">Sign in to keep this across devices. <a data-account>Sign in</a></div>`}`;
}
/** Signed out: sign in or sign up by email. New: choose a username. Signed in: profile switch, sign out, delete. */
function accountHTML() {
  if (!me)
    return `<h3>Account</h3>
    <div id="signin-box"><p class="lead">Sign in or create an account</p>
    <form class="line-form" id="signin"><input name="email" type="email" placeholder="Email address" required autocomplete="email"><button class="btn on" type="submit">Continue</button></form>
    <div class="hint">We'll email you a sign-in link. No password needed.</div></div>`;
  const body = me.handle
    ? `<div class="row acct"><span><span class="handle">@${esc(me.handle)}</span><br><span class="hint">${esc(me.email)}</span></span><label class="switch" title="Let others add you to their board"><input type="checkbox" id="pub" ${me.public ? "checked" : ""}><i></i><span>Public profile</span></label></div>
       <div class="hint">${me.public ? `Anyone can add @${esc(me.handle)} to their board and see your learning goals.` : "Only you can see your activity."}</div>`
    : `<p class="lead">Welcome! Choose a username to finish setting up.</p>
       <form class="line-form" id="handle-form"><input name="handle" placeholder="username" maxlength="24" autocomplete="username" spellcheck="false" required><button class="btn on" type="submit">Save</button></form>
       <div class="hint" id="handle-hint">3 to 24 characters: lowercase letters, numbers and hyphens.</div>`;
  return `<h3>Account</h3>
    ${body}
    <div class="hint acct-foot"><a data-signout>Sign out</a> · <a data-delete>Delete account</a></div>`;
}
const accountEl = $("#account");
function openAccount() {
  accountEl.hidden = false;
  closeAsk();
  renderAccount();
}
function closeAccount() {
  accountEl.hidden = true;
}
function renderAccount() {
  if (accountEl.hidden) return;
  $("#account-body").innerHTML = accountHTML();
  const first = $<HTMLInputElement>("#account-body input");
  if (first) setTimeout(() => first.focus(), 30);
}
/** The people I added, each a line, plus a box to add one by username. */
function boardHTML() {
  const rows = people
    .map((p) => {
      const names = p.goals.slice(0, 3).map((t) => t.name).join(", ") + (p.goals.length > 3 ? ` +${p.goals.length - 3}` : "");
      const pc = paceOf(Object.keys(p.days).sort());
      return `<li><div class="row"><span class="go" data-person="${esc(p.handle)}">@${esc(p.handle)}</span><span class="pace">${esc(names ? (pc ? `${names} · ${pc}` : names) : "No learning goals yet")}</span><a class="goto" data-unfollow="${esc(p.handle)}" title="Remove from board">×</a></div></li>`;
    })
    .join("");
  return `<h3>Board</h3>
    ${rows ? `<ul class="focus">${rows}</ul>` : ""}
    <form class="line-form" id="add-person"><input name="handle" placeholder="Add by username" maxlength="25" autocomplete="off" spellcheck="false" required><button class="btn quiet" type="submit">Add</button></form>
    <div class="hint" id="add-hint">${rows ? "" : "Add someone with a public profile to see their learning goals and streaks."}</div>`;
}
function openHome() {
  homeOpen = true;
  card = null;
  personOpen = null;
  focused = null;
  document.body.classList.add("panel-open");
  closeAsk();
  renderHome();
  renderMap();
  history.replaceState(null, "", "#you");
}
/** Someone's public view: heatmap and learning goals with their resources and last two weeks. */
const personViews = new Map<string, Person>();
async function openPerson(handle: string) {
  let p = people.find((x) => x.handle === handle) ?? personViews.get(handle);
  if (!p) {
    const res = await fetch(`/api/u/${encodeURIComponent(handle)}`).catch(() => null);
    if (!res?.ok) return toast("No public profile with that username.");
    p = (await res.json()) as Person;
    personViews.set(handle, p);
  }
  personOpen = handle;
  homeOpen = false;
  card = null;
  focused = null;
  document.body.classList.add("panel-open");
  closeAsk();
  renderPanel();
  renderMap();
  history.replaceState(null, "", `#@${handle}`);
}
function renderPerson() {
  const p = people.find((x) => x.handle === personOpen) ?? personViews.get(personOpen!);
  if (!p) return;
  const onBoard = people.some((x) => x.handle === p.handle);
  const rows = p.goals
    .map((t) => {
      const set = new Set(t.days);
      const res = [...t.using.map((r) => ({ r, done: false })), ...t.done.map((r) => ({ r, done: true }))]
        .map(({ r, done }) => `<li class="${done ? "done" : ""}">${r.url ? `<a href="${esc(r.url)}" target="_blank" rel="noopener">${esc(r.title)}</a>` : esc(r.title)}<span class="m">${esc(done ? "finished" : (kindWord[r.kind] ?? ""))}</span></li>`)
        .join("");
      return `<li class="open"><div class="row"><span class="go" data-open="${esc(t.slug)}">${esc(t.name)}</span><span class="pace">${esc(paceOf(t.days))}</span></div>${res ? `<ul class="using-list">${res}</ul>` : ""}${stripHTML(t.slug, (d) => set.has(d), false)}</li>`;
    })
    .join("");
  const summary = p.goals.length ? `${p.goals.map((t) => t.name).join(", ")} · ${paceOf(Object.keys(p.days).sort()) || "quiet"}` : "No learning goals yet";
  $("#panel-body").innerHTML = `
    <h2>@${esc(p.handle)}</h2>
    <p class="summary">${esc(summary)}</p>
    ${rows ? `<h3>Learning goals</h3><ul class="focus">${rows}</ul>` : ""}
    ${Object.keys(p.days).length ? heatHTML((iso) => p.days[iso] ?? 0) : ""}
    <div class="hint acct-foot">${onBoard ? `<a data-unfollow="${esc(p.handle)}">Remove from board</a>` : me ? `<a data-follow="${esc(p.handle)}">Add to board</a>` : "Sign in to add them to your board."}</div>`;
}
function renderPanel() {
  if (personOpen) return renderPerson();
  if (homeOpen) return renderHome();
  if (!card) return;
  const c = card, s = c.subject, m = markOf(s.slug);
  const lensesOn = new Set(state.lensesOn[s.slug] ?? []);
  const activeLenses = c.lenses.filter((l) => lensesOn.has(l.slug));
  const removed = new Set(activeLenses.flatMap((l) => l.removes.filter((x) => x.stage === stage).map((x) => x.resource.id)));
  const adds = activeLenses.flatMap((l) => l.adds.filter((x) => x.stage === stage).map((x) => ({ lens: l, x })));
  const stageData = c.stages[stage];
  const pool = showMore ? [...stageData.picks, ...stageData.more] : stageData.picks;
  const visibleGen = (r: Rec) => state.showGenerated || r.provenance !== "generated";
  const removedBy = new Map(activeLenses.flatMap((l) => l.removes.filter((x) => x.stage === stage).map((x) => [x.resource.id, l.name] as [string, string])));
  let picksHTML = pool
    .filter(visibleGen)
    .map((r) => pickHTML(r, removed.has(r.resource.id) ? { gone: true, note: `not with ${removedBy.get(r.resource.id)}` } : {}))
    .join("");
  for (const { lens, x } of adds) {
    if (pool.some((r) => r.resource.id === x.resource.id)) continue;
    const fake: Rec = { id: `lens:${lens.id}:${x.resource.id}`, stage, why: x.why ?? lens.summary, fit: null, used: 0, provenance: lens.provenance, resource: x.resource };
    const instead = x.insteadOf ? pool.find((r) => r.resource.id === x.insteadOf)?.resource.title : null;
    picksHTML += pickHTML(fake, { lens: lens.name, note: instead ? `instead of ${instead}` : "" });
  }
  const stageTabs = STAGES.map((st) => `<button class="${st === stage ? "on" : ""}" data-stage="${st}" ${c.stages[st].picks.length || c.stages[st].more.length ? "" : "disabled"}>${STAGE_LABEL[st]}</button>`).join("");
  const showAsk = (m === "comfortable" || m === "deep") && !state.asked[s.slug];
  const before = c.helps_in
    .map((h) => {
      const hm = markOf(h.subject.slug), ok = hm === "comfortable" || hm === "deep";
      return `<li data-open="${h.subject.slug}"><span>${esc(h.subject.name)} ${h.strength >= c.hard ? '<span class="need">needed</span>' : ""}</span><span class="st ${ok ? "ok" : ""}">${ok ? "✓" : hm === "new" ? "" : STATUS_LABEL[hm].toLowerCase()}</span></li>`;
    })
    .join("");
  const inside = c.children
    .map((k) => `<li data-open="${k.slug}"><span class="${k.provenance === "generated" ? "gen" : ""}">${esc(k.name)}</span><span class="st ${known(k.slug) ? "ok" : ""}">${known(k.slug) ? "✓" : markOf(k.slug) === "new" ? "" : STATUS_LABEL[markOf(k.slug)].toLowerCase()}</span></li>`)
    .join("");
  const lensChips = c.lenses
    .filter((l) => state.showGenerated || l.provenance !== "generated" || lensesOn.has(l.slug))
    .map((l) => `<button class="chip ${lensesOn.has(l.slug) ? "on" : ""} ${l.provenance === "generated" ? "gen" : ""}" data-lens="${l.slug}" title="${esc(l.summary)}">${esc(l.name)}</button>`)
    .join("");
  const hasMore = !showMore && stageData.more.length > 0;
  $("#panel-body").innerHTML = `
    <div class="chain">${c.chain.map((x) => `<a data-open="${x.slug}">${esc(x.name)}</a> ›`).join(" ")}</div>
    <h2 class="${s.provenance === "generated" ? "gen" : ""}">${esc(s.name)}</h2>
    <p class="summary">${esc(s.summary)}</p>
    ${s.learnable ? `<h3>where are you?</h3><div class="chips">${STATUSES.map((st) => `<button class="chip ${m === st ? (st === "new" ? "on" : "ok") : ""}" data-mark="${st}">${STATUS_LABEL[st]}</button>`).join("")}</div>
    <div class="track-line"><button class="chip ${goalOf(s.slug) ? "ok" : ""}" data-goal="${s.slug}">${goalOf(s.slug) ? "✓ Learning goal" : "Add as a learning goal"}</button>${goalOf(s.slug) ? `<span class="pace">${esc(pace(s.slug))}</span> <a class="go-you" data-you>${esc(myName())}</a>` : ""}</div>
    ${showAsk ? `<div class="ask-line">know a good one? <a data-ask-yes>recommend</a> · <a data-ask-no>not now</a></div>` : ""}` : ""}
    ${lensChips ? `<h3>lenses</h3><div class="chips">${lensChips}</div>` : ""}
    <h3>picks</h3>
    <div class="stages">${stageTabs}<label class="switch small" title="Show generated picks and lenses"><input type="checkbox" id="gen" ${state.showGenerated ? "checked" : ""}><i></i><span>generated</span></label></div>
    <ul class="picks">${picksHTML || `<li><div class="body"><div class="why">${state.showGenerated ? "nothing yet" : "nothing human yet"}</div></div></li>`}</ul>
    ${hasMore ? `<span class="more" data-more>more</span>` : ""}
    ${before ? `<h3>prereqs</h3><ul class="list">${before}</ul>` : ""}
    ${inside ? `<h3>inside</h3><ul class="list">${inside}</ul>` : ""}
    <button class="btn quiet" id="rec-open" ${recOpen ? "hidden" : ""}>add a recommendation</button>
    <form class="rec" id="rec-form" ${recOpen ? "" : "hidden"}>
      <input name="url" placeholder="link" inputmode="url" autocomplete="off">
      <input name="title" placeholder="title" autocomplete="off">
      <input name="why" placeholder="why, in a few words" maxlength="90" required autocomplete="off">
      <div class="chips" id="rec-kinds">${KINDS.map((k) => `<button type="button" class="chip small" data-rec-kind="${k}">${k}</button>`).join("")}</div>
      <div class="chips">${STAGES.map((st) => `<button type="button" class="chip ${st === stage ? "on" : ""}" data-rec-stage="${st}">${STAGE_LABEL[st]}</button>`).join("")}</div>
      <span class="more" data-rec-more>more</span>
      <div class="rec-more" hidden>
        <input name="author" placeholder="author" autocomplete="off">
        <input name="hours" placeholder="hours, roughly" inputmode="decimal" autocomplete="off">
      </div>
      <div class="row"><span class="err" id="rec-err"></span><span><button class="btn quiet" type="button" id="rec-cancel">cancel</button> <button class="btn on" type="submit">add</button></span></div>
    </form>`;
}

// ---------------------------------------------------------------- landing sheet
const askEl = $("#ask"), qEl = $<HTMLInputElement>("#q"), hitsEl = $("#hits");
let cur = 0;
function openAsk() {
  askEl.hidden = false;
  qEl.value = "";
  hitsEl.innerHTML = "";
  $("#examples").hidden = false;
  setTimeout(() => qEl.focus(), 30);
}
function closeAsk() {
  askEl.hidden = true;
}
type SearchItem = { slug: string; name: string; aliases: string[]; learnable?: boolean; provenance: string; subject: GSubject; lens?: GLens };
function searchIndex(): SearchItem[] {
  const out: SearchItem[] = graph.subjects.map((s) => ({ slug: s.slug, name: s.name, aliases: s.aliases, learnable: s.learnable, provenance: s.provenance, subject: s }));
  for (const l of graph.lenses ?? []) {
    const subject = byId.get(l.subjectId);
    if (!subject) continue;
    // "Piano · Faber" matches "faber", "piano faber" and "faber piano"
    out.push({ slug: `${subject.slug}+${l.slug}`, name: `${subject.name} · ${l.name}`, aliases: [l.name, `${l.name} ${subject.name}`], learnable: true, provenance: l.provenance, subject, lens: l });
  }
  return out;
}
function renderHits(q: string) {
  const hits = q.trim() ? match(q, searchIndex(), 7) : [];
  cur = 0;
  $("#examples").hidden = Boolean(q.trim());
  const rows = hits.map(
    (h, i) =>
      `<li class="${i === cur ? "cur" : ""}" data-open="${h.item.subject.slug}" ${h.item.lens ? `data-with-lens="${h.item.lens.slug}"` : ""}><b class="${h.item.provenance === "generated" ? "gen" : ""}">${esc(h.item.subject.name)}</b>${h.item.lens ? `<span class="lens-tag">${esc(h.item.lens.name)}</span>` : ""}<small>${esc(chainOf(h.item.subject).map((x) => x.name).join(" › "))}</small></li>`,
  );
  const strong = hits[0] && hits[0].score >= 0.8 && wordCount(q) < PARSE_MIN_WORDS;
  if (q.trim().length >= 3 && !strong) rows.push(`<li class="more" data-ask-go>${hits.length ? "something else →" : "find it →"}</li>`);
  hitsEl.innerHTML = rows.join("");
}
async function askGo(q: string) {
  askEl.classList.add("busy");
  $("#ask-hint").textContent = "finding it…";
  try {
    const res = await fetch("/api/ask", { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ q }) });
    const json = (await res.json()) as { slug?: string; error?: string; mentions?: { slug: string; status: Status | null }[]; created?: boolean };
    if (!res.ok || !json.slug) throw new Error(json.error ?? "no luck");
    for (const m of json.mentions ?? []) if (m.status && !state.marks[m.slug]) state.marks[m.slug] = m.status;
    if (json.created) await reloadGraph();
    await open(json.slug);
    if (json.created) toast("new, generated");
  } catch (e) {
    $("#ask-hint").textContent = e instanceof Error ? e.message : "no luck";
  } finally {
    askEl.classList.remove("busy");
  }
}
async function reloadGraph() {
  const g = (await fetch("/api/graph").then((r) => r.json())) as Graph;
  for (const s of g.subjects) {
    if (byId.has(s.id)) continue;
    graph.subjects.push(s);
    byId.set(s.id, s);
    bySlug.set(s.slug, s);
    const n: Node = { id: s.id, depth: 0, w: 196, h: 64, x: 0, y: 0, tx: 0, ty: 0, children: [] };
    nodeById.set(s.id, n);
  }
  for (const s of g.subjects) {
    const n = nodeById.get(s.id)!;
    if (n.parent) continue;
    const parent = s.parentId ? nodeById.get(s.parentId) : undefined;
    (parent ?? root).children.push(n);
    n.parent = parent ?? root;
    n.x = n.tx = parent?.tx ?? 0;
    n.y = n.ty = parent?.ty ?? 0;
  }
  graph.helps = g.helps;
  graph.lenses = g.lenses ?? [];
  helpsInto.clear();
  helpsFrom.clear();
  for (const h of graph.helps) {
    helpsInto.set(h.to, [...(helpsInto.get(h.to) ?? []), h]);
    helpsFrom.set(h.from, [...(helpsFrom.get(h.from) ?? []), h]);
  }
  setDepth(root, 0);
  root.w = 230;
  root.h = 84;
  canvas.nodes = [root, ...nodeById.values(), ...personNodes.values()];
  buildEdges();
  layout();
  renderMap();
}

// ---------------------------------------------------------------- actions
function toast(msg: string) {
  const t = $("#toast");
  t.textContent = msg;
  t.classList.add("show");
  clearTimeout((t as unknown as { _t: number })._t);
  (t as unknown as { _t: number })._t = setTimeout(() => t.classList.remove("show"), 2000) as unknown as number;
}
async function post(path: string, body: unknown) {
  const res = await fetch(path, { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify(body) });
  return { ok: res.ok, json: (await res.json().catch(() => ({}))) as { error?: string } };
}
function mark(slug: string, st: Status) {
  if (st === "new") delete state.marks[slug];
  else state.marks[slug] = st;
  if (card) stage = NEXT_STAGE[st];
  save(state);
  renderPanel();
  renderMap();
}
async function used(recId: string) {
  const undo = Boolean(state.used[recId]);
  if (undo) delete state.used[recId];
  else state.used[recId] = today();
  save(state);
  renderPanel();
  if (!recId.startsWith("lens:")) void post("/api/used", { rec_id: recId, undo });
}
function toggleLens(slug: string) {
  if (!card) return;
  const on = new Set(state.lensesOn[card.subject.slug] ?? []);
  if (on.has(slug)) on.delete(slug);
  else {
    on.add(slug);
    const l = card.lenses.find((x) => x.slug === slug);
    if (l) void post("/api/used", { lens_id: l.id });
  }
  state.lensesOn[card.subject.slug] = [...on];
  save(state);
  renderPanel();
}
async function recommend(form: HTMLFormElement) {
  if (!card) return;
  const fd = new FormData(form);
  const st = (form.querySelector(".chip.on[data-rec-stage]") as HTMLElement | null)?.dataset.recStage ?? stage;
  const kind = (form.querySelector(".chip.on[data-rec-kind]") as HTMLElement | null)?.dataset.recKind;
  const hoursRaw = Number(String(fd.get("hours") ?? "").replace(/[^0-9.]/g, ""));
  const body = {
    subject_id: card.subject.id,
    url: String(fd.get("url") ?? "").trim() || undefined,
    title: String(fd.get("title") ?? "").trim() || undefined,
    why: String(fd.get("why") ?? "").trim(),
    stage: st,
    kind,
    author: String(fd.get("author") ?? "").trim() || undefined,
    hours: hoursRaw > 0 ? hoursRaw : undefined,
  };
  const err = $("#rec-err");
  if (!body.url && !body.title) return void (err.textContent = "a link or a title");
  err.textContent = "";
  const r = await post("/api/recommend", body);
  if (!r.ok) return void (err.textContent = r.json.error ?? "no luck");
  state.asked[card.subject.slug] = true;
  save(state);
  toast("added, thank you");
  recOpen = false;
  await open(card.subject.slug, { center: false });
}

// ---------------------------------------------------------------- wiring
canvas.onNodeClick = (id) => {
  if (id === "root") return openHome();
  if (id.startsWith("@")) return void openPerson(id.slice(1));
  const s = byId.get(id);
  if (!s) return;
  if (nodeById.get(id)!.depth === 1 && zoomLevel === 1 && !focused) {
    // a domain far out: zoom into it rather than opening a card
    canvas.centerNode(nodeById.get(id)!, ZOOM_SUBJECTS + 0.05);
    return;
  }
  // stay put unless the card would sit under the panel or off-screen
  const n = nodeById.get(id)!;
  const sx = canvas.cam.tx + n.tx * canvas.cam.tk, sy = canvas.cam.ty + n.ty * canvas.cam.tk;
  const occluded = canvas.narrow() ? sy > innerHeight * 0.4 : sx > innerWidth - 440 - n.w * canvas.cam.tk || sx < 0 || sy < 64 || sy > innerHeight;
  void open(s.slug, { center: occluded });
};
canvas.onBackgroundClick = () => {
  if (card) closePanel();
};
canvas.onNodeMoved = (n) => {
  const s = byId.get(n.id);
  if (s) pinned.set(n.id, [n.tx, n.ty]);
  else if (!n.id.startsWith("@")) return;
  state.positions[s ? s.slug : n.id] = [n.tx, n.ty];
  save(state);
};
document.addEventListener("click", (e) => {
  const t = e.target as HTMLElement;
  const openEl = t.closest<HTMLElement>("[data-open]");
  if (openEl?.dataset.open && !t.closest("[data-day]")) {
    if (openEl.dataset.withLens) {
      const on = new Set(state.lensesOn[openEl.dataset.open] ?? []);
      on.add(openEl.dataset.withLens);
      state.lensesOn[openEl.dataset.open] = [...on];
      save(state);
    }
    return void open(openEl.dataset.open);
  }
  if (t.closest("[data-ask-go]")) return void askGo(qEl.value);
  const usedBtn = t.closest<HTMLElement>("[data-used]");
  if (usedBtn) return void used(usedBtn.dataset.used!);
  const useBtn = t.closest<HTMLElement>("[data-use]");
  if (useBtn && card) {
    const c = card;
    const r = STAGES.flatMap((st) => [...c.stages[st].picks, ...c.stages[st].more]).find((x) => x.id === useBtn.dataset.use);
    if (r) useRec(c.subject.slug, r);
    return;
  }
  const fin = t.closest<HTMLElement>("[data-finish]");
  if (fin && card) return finishRec(card.subject.slug, fin.dataset.finish!);
  if (t.closest("[data-account]")) return openAccount();
  if (t.closest("#account-close")) return closeAccount();
  if (t === accountEl) return closeAccount();
  if (t.closest("#zoom-in")) return canvas.zoomAt((innerWidth - canvas.panelWidth()) / 2, innerHeight / 2, 1.3);
  if (t.closest("#zoom-out")) return canvas.zoomAt((innerWidth - canvas.panelWidth()) / 2, innerHeight / 2, 1 / 1.3);
  const dayBtn = t.closest<HTMLElement>("[data-day]");
  if (dayBtn) return toggleDay(dayBtn.dataset.daySlug!, dayBtn.dataset.day!);
  const gl = t.closest<HTMLElement>("[data-goal]");
  if (gl) return toggleGoal(gl.dataset.goal!);
  const stripRow = t.closest<HTMLElement>("[data-strip]");
  if (stripRow && !t.closest("[data-open]")) {
    const slug = stripRow.dataset.strip!;
    if (openStrips.has(slug)) openStrips.delete(slug);
    else openStrips.add(slug);
    return renderPanel();
  }
  if (t.closest("#you")) return me?.handle ? (homeOpen ? closePanel() : openHome()) : openAccount();
  if (t.closest("[data-you]")) return openHome();
  const person = t.closest<HTMLElement>("[data-person]");
  if (person) return void openPerson(person.dataset.person!);
  const unfollow = t.closest<HTMLElement>("[data-unfollow]");
  if (unfollow) return void board(unfollow.dataset.unfollow!, true);
  const follow = t.closest<HTMLElement>("[data-follow]");
  if (follow) return void board(follow.dataset.follow!, false);
  if (t.closest("[data-signout]")) return void signOut();
  if (t.closest("[data-delete]")) {
    if (confirm("Delete your account? Your marks, learning goals and history will be permanently removed.")) void signOut(true);
    return;
  }
  const th = t.closest<HTMLElement>("[data-thread]");
  if (th) {
    const id = th.dataset.thread!;
    if (openThreads.has(id)) openThreads.delete(id);
    else {
      openThreads.add(id);
      if (!threads.has(id)) void loadThread(id);
    }
    return renderPanel();
  }
  const hb = t.closest<HTMLElement>("[data-helpful]");
  if (hb) {
    const id = hb.dataset.helpful!;
    const undo = Boolean(state.helpful[id]);
    if (undo) delete state.helpful[id];
    else state.helpful[id] = true;
    save(state);
    for (const list of threads.values()) for (const cm of list) if (cm.id === id) cm.helpful = Math.max(0, cm.helpful + (undo ? -1 : 1));
    renderPanel();
    void post("/api/helpful", { comment_id: id, undo });
    return;
  }
  const fl = t.closest<HTMLElement>("[data-flag]");
  if (fl) {
    const reasons = fl.parentElement!.querySelector<HTMLElement>(".flag-reasons")!;
    reasons.hidden = !reasons.hidden;
    return;
  }
  const fr = t.closest<HTMLElement>("[data-flag-reason]");
  if (fr) {
    const recId = fr.closest<HTMLElement>(".thread")!.parentElement!.closest<HTMLElement>("[data-rec]")!.dataset.rec!;
    void post("/api/flag", { rec_id: recId, reason: fr.dataset.flagReason });
    fr.closest<HTMLElement>(".thread-foot")!.innerHTML = '<span class="muted">flagged, thank you</span>';
    return;
  }
  const rk = t.closest<HTMLElement>("[data-rec-kind]");
  if (rk) {
    rk.parentElement!.querySelectorAll(".chip").forEach((c) => c.classList.remove("on"));
    rk.classList.add("on");
    return;
  }
  if (t.closest("[data-rec-more]")) {
    $(".rec-more").hidden = false;
    (t as HTMLElement).remove();
    return;
  }
  if (t.closest("#rec-open")) {
    recOpen = true;
    renderPanel();
    $<HTMLInputElement>('#rec-form input[name="url"]').focus();
    return;
  }
  if (t.closest("#rec-cancel")) {
    recOpen = false;
    return renderPanel();
  }
  const markBtn = t.closest<HTMLElement>("[data-mark]");
  if (markBtn && card) return mark(card.subject.slug, markBtn.dataset.mark as Status);
  const lensBtn = t.closest<HTMLElement>("[data-lens]");
  if (lensBtn) return toggleLens(lensBtn.dataset.lens!);
  const stBtn = t.closest<HTMLElement>("[data-stage]");
  if (stBtn && !(stBtn as HTMLButtonElement).disabled) {
    stage = stBtn.dataset.stage as Stage;
    showMore = false;
    return renderPanel();
  }
  const rs = t.closest<HTMLElement>("[data-rec-stage]");
  if (rs) {
    rs.parentElement!.querySelectorAll(".chip").forEach((c) => c.classList.remove("on"));
    rs.classList.add("on");
    return;
  }
  if (t.closest("[data-more]")) {
    showMore = true;
    return renderPanel();
  }
  if (t.closest("[data-ask-yes]")) {
    recOpen = true;
    renderPanel();
    $("#rec-form").scrollIntoView({ behavior: "smooth", block: "center" });
    $<HTMLInputElement>('#rec-form input[name="url"]').focus();
    return;
  }
  if (t.closest("[data-ask-no]") && card) {
    state.asked[card.subject.slug] = true;
    save(state);
    return renderPanel();
  }
  if (t.closest("#close")) return closePanel();
  if (t.closest("#ask-close")) return closeAsk();
  if (t.closest("#title")) return openAsk();
  if (t.closest("#fit")) return canvas.fitAll();
  if (t.closest("[data-example]")) return void open((t.closest<HTMLElement>("[data-example]")!).dataset.example!);

  if (t === askEl) closeAsk();
});
async function peek(input: HTMLInputElement) {
  const form = input.form!;
  const url = input.value.trim();
  if (!/^https?:\/\//.test(url)) return;
  const res = await fetch(`/api/peek?url=${encodeURIComponent(url)}`).catch(() => null);
  if (!res?.ok) return;
  const { title, kind } = (await res.json()) as { title: string | null; kind: string };
  const titleEl = form.querySelector<HTMLInputElement>('input[name="title"]')!;
  if (title && !titleEl.value) titleEl.value = title;
  form.querySelectorAll("[data-rec-kind]").forEach((c) => c.classList.toggle("on", (c as HTMLElement).dataset.recKind === kind));
}
/** Add someone to my board, or take them off. */
async function board(handle: string, remove: boolean) {
  const r = await post("/api/me/board", { handle, remove });
  const hint = document.querySelector("#add-hint");
  if (!r.ok) {
    if (hint) hint.textContent = r.json.error ?? "Something went wrong. Please try again.";
    else toast(r.json.error ?? "Something went wrong. Please try again.");
    return;
  }
  await loadBoard();
  renderPanel();
}
async function signIn(form: HTMLFormElement) {
  const email = form.querySelector<HTMLInputElement>('input[name="email"]')!.value.trim();
  form.querySelector("button")!.disabled = true;
  const r = await post("/api/auth/link", { email });
  if (!r.ok) {
    form.querySelector("button")!.disabled = false;
    return toast(r.json.error ?? "Something went wrong. Please try again.");
  }
  const link = (r.json as { link?: string }).link;
  $("#signin-box").innerHTML = `<p class="lead">Check your email</p><p class="summary">We sent a sign-in link to ${esc(email)}. It expires in 15 minutes.${link ? ` <a href="${esc(link)}">Open the link</a>` : ""}</p><div class="hint"><a data-account>Use a different address</a></div>`;
}
async function setHandle(form: HTMLFormElement) {
  const handle = form.querySelector<HTMLInputElement>('input[name="handle"]')!.value.trim().toLowerCase().replace(/^@/, "");
  const r = await post("/api/me/handle", { handle });
  if (!r.ok) return void ($("#handle-hint").textContent = r.json.error ?? "Something went wrong. Please try again.");
  if (me) me.handle = handle;
  closeAccount();
  renderMap();
  openHome();
  toast(`Welcome, @${handle}`);
}
document.addEventListener("change", (e) => {
  const t = e.target as HTMLInputElement;
  if (t.matches('#rec-form input[name="url"]')) void peek(t);
  if (t.id === "pub" && me) {
    me.public = t.checked;
    void post("/api/me/public", { public: t.checked }).then(() => renderAccount());
  }
  if (t.id === "gen") {
    state.showGenerated = t.checked;
    save(state);
    renderPanel();
  }
});
document.addEventListener("submit", (e) => {
  const form = e.target as HTMLFormElement;
  if (form.id === "rec-form") {
    e.preventDefault();
    void recommend(form);
  } else if (form.id === "signin") {
    e.preventDefault();
    void signIn(form);
  } else if (form.id === "handle-form") {
    e.preventDefault();
    void setHandle(form);
  } else if (form.id === "add-person") {
    e.preventDefault();
    void board(form.querySelector<HTMLInputElement>('input[name="handle"]')!.value, false);
  } else if (form.dataset.threadForm) {
    e.preventDefault();
    const recId = form.dataset.threadForm;
    const body = (form.querySelector<HTMLInputElement>('input[name="body"]')?.value ?? "").trim();
    if (body.length < 2) return;
    void post("/api/comment", { rec_id: recId, body }).then((r) => {
      if (!r.ok) return toast(r.json.error ?? "no luck");
      toast("added, thank you");
      threads.delete(recId);
      void loadThread(recId);
      if (card) void open(card.subject.slug, { center: false });
    });
  }
});
qEl.addEventListener("input", () => renderHits(qEl.value));
qEl.addEventListener("keydown", (e) => {
  const rows = [...hitsEl.querySelectorAll("li")];
  if (e.key === "ArrowDown" || e.key === "ArrowUp") {
    e.preventDefault();
    cur = (cur + (e.key === "ArrowDown" ? 1 : rows.length - 1)) % Math.max(rows.length, 1);
    rows.forEach((r, i) => r.classList.toggle("cur", i === cur));
  } else if (e.key === "Enter") {
    e.preventDefault();
    const row = rows[cur];
    if (row?.dataset.open) row.click();
    else if (qEl.value.trim().length >= 3) void askGo(qEl.value);
  }
});
$<HTMLInputElement>("#search").addEventListener("focus", () => {
  openAsk();
  $<HTMLInputElement>("#search").blur();
});
addEventListener("keydown", (e) => {
  if (e.key === "Escape") {
    if (!accountEl.hidden) closeAccount();
    else if (!askEl.hidden) closeAsk();
    else if (card || homeOpen) closePanel();
  }
  if (e.key === "/" && askEl.hidden && !(e.target as HTMLElement).matches("input,textarea")) {
    e.preventDefault();
    openAsk();
  }
  if (e.key === "f" && askEl.hidden && !(e.target as HTMLElement).matches("input,textarea")) canvas.fitAll();
});

addEventListener("hashchange", () => {
  const slug = location.hash.slice(1);
  if (slug.startsWith("@")) void openPerson(slug.slice(1));
  else if (slug && bySlug.has(slug) && card?.subject.slug !== slug) void open(slug);
});

// ---------------------------------------------------------------- go
buildEdges();
renderMap();
layout();
await loadMe();
requestAnimationFrame(() => {
  renderMap();
  layout();
  canvas.fitAll(true);
  const slug = location.hash.slice(1);
  if (slug === "you") openHome();
  else if (slug === "welcome") {
    history.replaceState(null, "", location.pathname);
    if (me?.handle) openHome();
    else openAccount();
    toast("Signed in");
  } else if (slug === "signin-expired") {
    history.replaceState(null, "", location.pathname);
    openAccount();
    toast("That sign-in link has expired. Request a new one.");
  } else if (slug.startsWith("@")) {
    closeAsk();
    void openPerson(slug.slice(1));
  } else if (slug && bySlug.has(slug)) {
    closeAsk();
    void open(slug);
  } else if (goals().length) openHome(); // returning: progress first
  else if (state.recent.length) closeAsk();
});
